import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import DisplayBox from "components/DisplayBox";
import RenderIconWithText, { RENDER_SIZE } from "components/DisplayBox/Renderers/RenderIconWithText";
import { ICON_SIZE, ICON_TYPE, STATUS_ICON_VARIANT, StatusIcon } from "components/Icon";
import { colors, margins } from "style";

/**
 * ProgressChecklist
 *
 * @param {Array}  steps        Each with `isComplete` for a finished step or `isActive` for the one in progress
 * @param {String} title
 * @param {Object} titleValues
 * @param {String} size
 */
const ProgressChecklist = ({ steps, title, titleValues, size, ...props }) => (
  <DisplayBox
    hideHeader={!title}
    title={<FormattedMessage id={title} values={titleValues} />}
    numColumns={1}
    rowGap={0}
    marginSize={margins.none}
    data={steps?.map(({ title, label, isComplete, isActive, ...rest }) => ({
      ...((title || label) && {
        title: title || (
          <span css={styles.label(isComplete || isActive)}>
            <FormattedMessage id={label} />
          </span>
        ),
      }),
      isComplete,
      isActive,
      ...rest,
    }))}
    itemRenderer={({ isComplete, isActive, onClick, children, selected, ...text }) => (
      <>
        <RenderIconWithText
          size={size}
          icon={
            <StatusIcon
              type={ICON_TYPE.line}
              size={ICON_SIZE.large}
              color={colors.grayAnatomyLight2}
              isComplete={isComplete}
              variant={isActive ? STATUS_ICON_VARIANT.active : undefined}
            />
          }
          onClick={onClick}
          text={text}
        />
        {children && selected && <div css={styles.children}>{children}</div>}
      </>
    )}
    {...props}
  />
);

const styles = {
  label: (isHighlighted) => css`
    font-weight: ${isHighlighted ? `500` : `400`};
    color: ${colors.purpleRainDark2};
  `,
  children: css`
    margin-left: 4.2rem;
    border-bottom: 1px ${colors.grayAnatomyLight4} solid;
  `,
};

ProgressChecklist.propTypes = {
  size: PropTypes.oneOf(Object.values(RENDER_SIZE)),
  steps: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  titleValues: PropTypes.object,
};

ProgressChecklist.defaultProps = {
  size: RENDER_SIZE.small,
};

export default ProgressChecklist;
